import { useEffect, useState } from 'react'
import { ArrowUpRight, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { PublicPageShell } from '@/features/marketing/components/PublicPageShell'
import { ReleaseNoteCard } from './ReleaseNoteCard'
import { usePortalSession } from './usePortalSession'
import { fetchMyRatings, fetchReleases, type ReleaseNote } from './releases'

/** Lo que el histórico lee del `state` cuando la nota pedida no se encontró. */
export interface EstadoNotaNoEncontrada {
  notaNoEncontrada: true
}

/**
 * UNA nota de versión, abierta y destacada: lo que se ve al pulsar el enlace
 * del aviso de WhatsApp, `/actualizaciones?repo=<repo>&version=<versión>`
 * (Víctor, 2026-10-21: el enlace lleva a la nota que se anuncia, no al
 * listado). La monta `ReleasesHistoryPage` cuando la URL trae las dos cosas.
 *
 * Tres cosas que no son estilo:
 *
 * 1. **Se pide por `repo` + `version`, no por `id`**: es la identidad estable
 *    de la nota (`@@unique(repo, version)` en el portal-server). El borrado y
 *    re-post con el que el skill de promoción corrige una nota le cambia el
 *    UUID, y el enlace ya está en el grupo de WhatsApp.
 * 2. **El servidor filtra; aquí solo se COMPRUEBA** que lo que vuelve es lo
 *    pedido. Un portal-server anterior al filtro `version` lo ignora y devuelve
 *    la nota más reciente de la aplicación: si no es esa versión, no se finge
 *    que lo es.
 * 3. **Una nota que no está —o un servidor que no contesta— cae al histórico
 *    de esa aplicación** (`?repo=`, con `replace`), con un aviso de una línea.
 *    Un repo que el histórico no conoce cae a su vez a «Todas»: nunca una
 *    pantalla rota ni un 404.
 *
 * Es pública, como el histórico: entrar en frío desde WhatsApp no pide sesión.
 * El voto propio se hidrata después y en diferido, igual que en el listado.
 */
export function FeaturedReleaseNote({
  repo,
  version,
}: {
  repo: string
  version: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { ensureSession } = usePortalSession()
  const [nota, setNota] = useState<ReleaseNote | null>(null)
  const [myScore, setMyScore] = useState<number | null>(null)

  useEffect(() => {
    let active = true
    ;(async () => {
      let encontrada: ReleaseNote | undefined
      try {
        const result = await fetchReleases({ repo, version, pageSize: 1 })
        encontrada = result.items.find(
          (note) => note.repo === repo && note.version === version,
        )
      } catch {
        // Servidor caído: se trata como no encontrada y cae al histórico.
      }
      if (!active) return
      if (!encontrada) {
        const estado: EstadoNotaNoEncontrada = { notaNoEncontrada: true }
        navigate(
          { search: `?${new URLSearchParams({ repo })}` },
          { replace: true, state: estado },
        )
        return
      }
      const { id } = encontrada
      setNota(encontrada)
      try {
        const token = await ensureSession()
        if (!token || !active) return
        const mine = await fetchMyRatings([id])
        if (!active) return
        setMyScore(mine.find((m) => m.releaseNoteId === id)?.score ?? null)
      } catch {
        // Sin sesión o sin votos: se queda con el promedio público.
      }
    })()
    return () => {
      active = false
    }
  }, [repo, version, navigate, ensureSession])

  return (
    <PublicPageShell
      backTo="/actualizaciones"
      backLabel={t('releases.allUpdates')}
    >
      <div className="mb-10">
        <span className="text-xs font-bold uppercase tracking-widest text-primary">
          {t('releases.badge')}
        </span>
        <h1 className="mt-2 text-3xl font-bold tracking-tight lg:text-4xl">
          {t('releases.historyTitle')}
        </h1>
      </div>

      {nota ? (
        <>
          <ReleaseNoteCard note={nota} myScore={myScore} destacada />
          <div className="mt-10 flex justify-center">
            <Link
              to="/actualizaciones"
              className="inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-2.5 text-sm font-bold text-background transition-colors hover:bg-primary"
            >
              {t('releases.seeMore')}
              <ArrowUpRight className="size-4" />
            </Link>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" />
          {t('common.loading')}
        </div>
      )}
    </PublicPageShell>
  )
}
